// Thin client for the conda brain service. In dev the browser calls it
// directly (CORS is enabled on conda; Chatwoot sets no CSP). For production,
// point CONDA_BASE at the bundled conda sidecar or proxy it through Rails.
export const CONDA_BASE =
  window.condaBaseUrl || import.meta.env.VITE_CONDA_BASE || 'http://localhost:4000';

async function req(path, opts = {}) {
  const res = await fetch(`${CONDA_BASE}/api${path}`, {
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
    ...opts,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `HTTP ${res.status}`);
  }
  if (res.status === 204) return undefined;
  return res.json();
}

export const condaApi = {
  health: () => fetch(`${CONDA_BASE}/health`).then(r => r.json()),

  // FAQ
  listKb: (q = '', category = '', status = '') =>
    req(`/kb?${new URLSearchParams({ q, category, status })}`),
  saveKb: entry =>
    entry.id
      ? req(`/kb/${encodeURIComponent(entry.slug)}`, { method: 'PUT', body: JSON.stringify(entry) })
      : req('/kb', { method: 'POST', body: JSON.stringify(entry) }),
  deleteKb: slug => req(`/kb/${encodeURIComponent(slug)}`, { method: 'DELETE' }),
  // Entries semantically similar to this draft (duplicate warning before create)
  kbSimilar: ({ description, response }) =>
    req('/kb/similar', { method: 'POST', body: JSON.stringify({ description, response }) }),
  categories: () => req('/categories'),

  // Documents
  listDocuments: () => req('/documents'),
  addUrl: (url, title) => req('/documents/url', { method: 'POST', body: JSON.stringify({ url, title }) }),
  uploadPdf: async (file, title) => {
    const fd = new FormData();
    fd.append('file', file);
    if (title) fd.append('title', title);
    const res = await fetch(`${CONDA_BASE}/api/documents/pdf`, { method: 'POST', body: fd });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
    return res.json();
  },
  reingest: id => req(`/documents/${id}/reingest`, { method: 'POST' }),
  deleteDocument: id => req(`/documents/${id}`, { method: 'DELETE' }),

  // Playground
  chat: (message, history, sessionId) =>
    req('/playground/chat', { method: 'POST', body: JSON.stringify({ message, history, sessionId }) }),

  // Assistant identity (name / brand / persona)
  getSettings: () => req('/settings'),
  saveSettings: settings => req('/settings', { method: 'PUT', body: JSON.stringify(settings) }),

  // Chat with Chanel (the Motion coach) about the turn under review
  chanel: payload => req('/motion/chanel', { method: 'POST', body: JSON.stringify(payload) }),
  // Training signal: weak bot turns to review and fix
  insights: () => req('/motion/insights'),
  // Resolve (or un-resolve) a flagged turn so it stops surfacing in Suggested
  resolveTurn: (messageId, resolved = true) =>
    req('/motion/resolve', { method: 'POST', body: JSON.stringify({ messageId, resolved }) }),
  // Review: real bot conversations — list (all) and full thread by id
  conversations: (params = {}) => req(`/conversations?${new URLSearchParams(params)}`),
  conversation: id => req(`/conversations/${id}`),
  // LLM summary of a conversation (server caches it until new messages arrive)
  conversationSummary: id => req(`/conversations/${id}/summary`),
  // Agent assist: draft a reply for the human agent on a live Chatwoot conversation
  assistSuggest: conversationId =>
    req('/assist/suggest', { method: 'POST', body: JSON.stringify({ conversationId }) }),
  // Inbox sidebar: summary + customer mood for a live conversation (cached server-side)
  assistInsight: (conversationId, refresh = false) =>
    req(`/assist/insight/${conversationId}${refresh ? '?refresh=1' : ''}`),
  // Agent playbook: patterns learned from human agents' handover replies
  playbook: () => req('/assist/playbook'),
  playbookDelete: id => req(`/assist/playbook/${id}`, { method: 'DELETE' }),
  // Promote a KB-worthy pattern: draft the entry, then (after operator review) create it
  playbookDraftKb: id => req(`/assist/playbook/${id}/draft-kb`, { method: 'POST' }),
  playbookPromote: (id, entry) =>
    req(`/assist/playbook/${id}/promote`, { method: 'POST', body: JSON.stringify(entry) }),
  // Draft a previewable change proposal, then apply it
  propose: payload => req('/motion/propose', { method: 'POST', body: JSON.stringify(payload) }),
  applyChanges: (changes, threadId) => req('/motion/apply', { method: 'POST', body: JSON.stringify({ changes, threadId }) }),
};
